import type { Currency, PriceType } from '../types/charge-point.types';

/**
 * Generates hour span string like "3:00 PM - 4:00 PM" or "15:00 - 16:00"
 */
export function generateSpotPriceHourSpan(
  timeString: string,
  locale: string
): string {
  const date = new Date(timeString);
  const endDate = new Date(date.getTime() + 60 * 60 * 1000); // Add 1 hour

  // Each locale's own clock: 12h for en/en-US, 24h for da/de/fr/es
  const formatOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
  };

  const formatter = new Intl.DateTimeFormat(locale, formatOptions);
  const startTime = formatter.format(date);
  const endTime = formatter.format(endDate);

  return `${startTime} - ${endTime}`;
}

/**
 * Formats price with proper currency and locale
 * Returns formatted number without currency symbol (e.g., "2,50" or "2.50")
 */
export function formatPrice(
  amount: number,
  currency: Currency,
  locale: string
): string {
  const decimals = currency.decimals ?? 2;

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
}

/**
 * Gets price type translation key
 */
export function getPriceTypeKey(priceType?: PriceType): string {
  if (priceType === 'spot') {
    return 'chargePoint.dynamicPrice';
  }
  return 'chargePoint.fixedPrice';
}

/**
 * Splits a price label at '/' for rich text formatting
 * Example: "2.50 DKK/kWh" => ["2.50 DKK", "/kWh"]
 */
export function splitPriceLabel(label?: string): string[] {
  if (!label) return [];

  const parts = label.split('/');
  if (parts.length > 1) {
    // Add '/' back to the last part
    parts[parts.length - 1] = `/${parts[parts.length - 1]}`;
  }

  return parts;
}

/**
 * Get price unit string based on price type
 */
export function getPriceUnitString(priceType?: PriceType): string {
  switch (priceType) {
    case 'fixed_kwh':
      return '/kWh';
    case 'fixed_min':
      return '/min';
    case 'fee':
      return '';
    case 'spot':
      return '/kWh';
    default:
      return '';
  }
}

/**
 * Get currency display string (symbol if available, otherwise uppercase identifier)
 */
export function getCurrencyDisplay(currency?: Currency): string {
  if (!currency) return 'DKK';

  // Use symbol if available
  if (currency.symbol) return currency.symbol;

  // Map common currency identifiers to symbols
  const currencySymbols: Record<string, string> = {
    USD: '$',
    CAD: '$',
    EUR: '€',
    GBP: '£',
    DKK: 'kr',
    SEK: 'kr',
    NOK: 'kr',
  };

  const upperIdentifier = currency.identifier.toUpperCase();
  return currencySymbols[upperIdentifier] || upperIdentifier;
}
